'use client';

// useChat - Hook for managing chat state and sending messages

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChatMessage, ChatHistoryResponse, ChatStreamEvent, StreamErrorEvent } from './types';

// Storage key for conversation persistence
const CHAT_STORAGE_KEY = 'boty_chat_messages';

/**
 * Query key factory for chat
 */
const queryKeys = {
  all: ['chat'] as const,
  history: (botId: string, sessionId: string) => [...queryKeys.all, botId, sessionId] as const,
};

/**
 * Generate a unique ID for messages
 */
function generateId(): string {
  return crypto.randomUUID();
}

/**
 * Save messages to localStorage for session recovery
 */
function saveMessagesToStorage(botId: string, messages: ChatMessage[]): void {
  if (typeof window === 'undefined') return;
  try {
    const key = `${CHAT_STORAGE_KEY}_${botId}`;
    localStorage.setItem(key, JSON.stringify(messages));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Load messages from localStorage
 */
function loadMessagesFromStorage(botId: string): ChatMessage[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const key = `${CHAT_STORAGE_KEY}_${botId}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/**
 * Clear messages from localStorage
 */
function clearMessagesFromStorage(botId: string): void {
  if (typeof window === 'undefined') return;
  try {
    const key = `${CHAT_STORAGE_KEY}_${botId}`;
    localStorage.removeItem(key);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Fetch chat history from API
 */
async function fetchChatHistory(botId: string, sessionId: string): Promise<ChatMessage[]> {
  const response = await fetch(`/api/bots/${botId}/chat?sessionId=${sessionId}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || 'Failed to fetch chat history');
  }

  const result: ChatHistoryResponse = await response.json();
  return result.data.messages;
}

export interface UseChatOptions {
  /** Bot ID to chat with */
  botId: string;
  /** Session ID for the conversation */
  sessionId: string;
  /** Whether to enable the hook */
  enabled?: boolean;
}

export interface UseChatReturn {
  /** All messages in the conversation */
  messages: ChatMessage[];
  /** Whether a message is being sent/streamed */
  isLoading: boolean;
  /** Error if any */
  error: Error | null;
  /** Send a new message */
  sendMessage: (content: string) => void;
  /** Clear all messages */
  clearChat: () => void;
  /** Whether chat history is being loaded */
  isHistoryLoading: boolean;
}

/**
 * Hook for managing chat state and sending messages with SSE streaming
 *
 * @param options - Hook options
 * @returns Chat state and functions
 *
 * @example
 * ```tsx
 * const { messages, isLoading, sendMessage } = useChat({
 *   botId: 'bot-123',
 *   sessionId: 'session-456',
 * });
 *
 * sendMessage('Hello, what are your hours?');
 * ```
 */
export function useChat({
  botId,
  sessionId,
  enabled = true,
}: UseChatOptions): UseChatReturn {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => queryKeys.history(botId, sessionId), [botId, sessionId]);

  // Local state for messages (optimistic updates during streaming)
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Ref to track loading state for the send guard (avoids stale closure issues)
  const isLoadingRef = useRef(false);

  // Ref to track current streaming message ID
  const streamingMessageIdRef = useRef<string | null>(null);

  // Query for chat history (disabled - we use localStorage for session recovery)
  // This query is kept for potential future use but not used to populate messages
  const { isLoading: isHistoryLoading } = useQuery({
    queryKey,
    queryFn: () => fetchChatHistory(botId, sessionId),
    enabled: false, // Disabled - localStorage is the source of truth for testing phase
    staleTime: Infinity,
  });

  // Track if we've initialized to prevent re-initialization
  const hasInitialized = useRef(false);

  // Initialize messages from localStorage on mount (one-time only)
  useEffect(() => {
    if (!botId || !sessionId || hasInitialized.current) return;
    hasInitialized.current = true;

    // Try to load from localStorage for session recovery
    const storedMessages = loadMessagesFromStorage(botId);
    if (storedMessages && storedMessages.length > 0) {
      setMessages(storedMessages);
    }
  }, [botId, sessionId]);

  // Save messages to storage when they change
  useEffect(() => {
    if (messages.length > 0 && !isLoading) {
      saveMessagesToStorage(botId, messages);
    }
  }, [messages, botId, isLoading]);

  /**
   * Send a message and handle SSE streaming response
   */
  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoadingRef.current) return;

      setError(null);
      setIsLoading(true);
      isLoadingRef.current = true;

      // Create user message
      const userMessage: ChatMessage = {
        id: generateId(),
        botId,
        sessionId,
        role: 'user',
        content: content.trim(),
        createdAt: new Date().toISOString(),
      };

      // Create placeholder assistant message
      const assistantMessageId = generateId();
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        botId,
        sessionId,
        role: 'assistant',
        content: '',
        createdAt: new Date().toISOString(),
        isStreaming: true,
      };

      streamingMessageIdRef.current = assistantMessageId;

      // Add both messages optimistically
      setMessages((prev) => [...prev, userMessage, assistantMessage]);

      try {
        const response = await fetch(`/api/bots/${botId}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: content, sessionId }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error?.message || 'Failed to send message');
        }

        // Read SSE stream
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let streamDone = false;

        while (!streamDone) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');

          // Keep the last incomplete line in buffer
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;

            const data = line.slice(6); // Remove 'data: ' prefix
            if (data === '[DONE]') {
              // Stream complete - mark assistant message and exit both loops.
              // We don't wait for the server to close the connection because the
              // server still does post-stream work (DB save, PostHog flush) that
              // can take seconds, especially on slow mobile networks. Reading
              // until close would keep isLoading=true and freeze the input.
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId ? { ...msg, isStreaming: false } : msg
                )
              );
              streamDone = true;
              break;
            }

            let parsed: ChatStreamEvent | StreamErrorEvent;
            try {
              parsed = JSON.parse(data) as ChatStreamEvent | StreamErrorEvent;
            } catch {
              // Ignore JSON parse errors for individual chunks
              continue;
            }

            // Check if it's an error event from the server
            if ('error' in parsed) {
              throw new Error(parsed.error.message);
            }

            // Update assistant message with new content
            if ('content' in parsed) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? { ...msg, content: msg.content + parsed.content }
                    : msg
                )
              );
            }
          }
        }

        // Release the reader so the connection can be cleaned up. The server
        // already received the message and is finishing its post-stream work
        // (DB save, analytics flush) — we don't need to keep the stream open.
        try {
          await reader.cancel();
        } catch {
          // Ignore — reader may already be closed
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
        setError(new Error(errorMessage));

        // Remove the placeholder assistant message on error
        setMessages((prev) => prev.filter((msg) => msg.id !== assistantMessageId));
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
        streamingMessageIdRef.current = null;
      }
    },
    [botId, sessionId]
  );

  /**
   * Clear all messages
   */
  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
    clearMessagesFromStorage(botId);
    queryClient.removeQueries({ queryKey });
  }, [botId, queryClient, queryKey]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearChat,
    isHistoryLoading,
  };
}

export default useChat;
